import React, { ReactNode, useId, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useModalContext } from '@/contexts/ModalProvider';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface AdaptiveModalProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  className?: string;
  size?: ModalSize;
  scrollable?: boolean;
  // Legacy props - ignored but kept for compatibility
  position?: string;
  variant?: string;
  showCloseButton?: boolean;
  blurBackground?: boolean;
  preventKeyboardAdjust?: boolean;
  trigger?: ReactNode;
  addBottomNavPadding?: boolean;
  keyboardHeight?: number;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  full: 'max-w-4xl',
};

export const AdaptiveModal: React.FC<AdaptiveModalProps> = ({ 
  children, 
  isOpen, 
  onClose, 
  title,
  className,
  size = 'md',
  scrollable = false,
}) => {
  const modalId = useId();
  const { registerModal, unregisterModal } = useModalContext();

  useEffect(() => {
    if (isOpen) {
      registerModal(modalId);
    } else {
      unregisterModal(modalId);
    }
    return () => unregisterModal(modalId);
  }, [isOpen, modalId, registerModal, unregisterModal]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn(sizeClasses[size], 'max-h-[85vh]', className)}>
        {title && (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        )}
        {scrollable ? (
          <ScrollArea className="max-h-[70vh] pr-4">
            {children}
          </ScrollArea>
        ) : (
          children
        )}
      </DialogContent>
    </Dialog>
  );
};
